import { DEFAULT_PALETTE, type ThemeColorKey } from "@/lib/branding/theme";

/* Dressing the sportsbook in Northstar's colours.

   BetBy's renderer builds into a shadow root and its styles carry concrete
   hex values rather than custom properties, so no stylesheet of ours can reach
   inside it. What decides every colour is a theme document the renderer
   fetches at startup from `${themesBaseUrl}${themeName}/theme.json` — 305 keys
   naming every part of the book.

   Both halves of that URL are ours to set, so rather than accept BetBy's blue
   we serve the document ourselves: fetch the theme they publish, swap its
   palette for the design system's, and hand back the result. Working from
   their file rather than a copy of it means a key they add later arrives
   already themed instead of silently reverting to blue.

   The lasting fix is a Northstar theme published on BetBy's own CDN, built in
   their theme editor. That needs a BetBy account, and until it exists this is
   what makes the book look like the rest of the site. `sportsbook.themed`
   turns it off and goes back to their CDN. */

export const BETBY_THEMES_BASE = "https://bt-app-static-themes.sptpub.com/master/";

/* Root-relative, so it resolves against whatever origin is serving the app.
   The renderer appends `<themeName>/theme.json` to it. */
export const NORTHSTAR_THEMES_BASE = "/betby-theme/";

/* The theme the shipped default and every read-time fallback resolve to when
   `sportsbook.themeName` is blank — a name our own route already serves. */
export const DEFAULT_THEME_NAME = "default-table";

/* The only names `recolourTheme`'s PALETTE actually maps — every other name
   BetBy would accept renders a book we have not themed. Ordered with
   `DEFAULT_THEME_NAME` first because it is `options[0]`, and the generic
   select coercion falls back to that on an invalid save. */
export const BETBY_THEME_NAMES: string[] = [DEFAULT_THEME_NAME, "default"];

export type Palette = Record<ThemeColorKey, string>;

type Swatch = [ThemeColorKey] | [ThemeColorKey, number];

/* BetBy's colour → the design token that plays the same part.

   Their palette is a dark teal ramp with a blue accent; ours is a neutral
   ramp with a violet one, so the mapping is by role and not by hue. Keys are
   compared with whitespace removed and lower-cased, because the same colour
   appears as both `rgba(255,255,255,0.5)` and `rgba(255, 255, 255, 0.5)`.

   Whites are deliberately absent. Northstar's foreground is white too, and
   BetBy's translucent whites sit over surfaces of many lightnesses — pinning
   them to a solid subtle grey would cost contrast on the darker ones. */
const PALETTE: Record<string, Swatch> = {
  /* Surfaces. Teal ramp → --color-canvas and the elevation steps. */
  "#12222b": ["canvas"] /* page            → canvas      */,
  "#1e313a": ["elevation1"] /* panel           → elevation-1 */,
  "#1e2d36": ["gray850"] /* panel, alt      → gray-850    */,
  "#253741": ["gray850"],
  "#2a3f4c": ["elevation2"] /* raised          → elevation-2 */,
  "#2a3941": ["elevation2"],
  "rgb(39,59,71)": ["elevation2"],
  "#35454e": ["elevation3"] /* border, hover   → elevation-3 */,
  "#354955": ["elevation3"],
  "#365c7b": ["elevation3"] /* content         → elevation-3 */,
  "#3b4e5d": ["elevation4"] /* highest         → elevation-4 */,
  "#3f525e": ["elevation4"],

  /* The same ramp again, as `default` spells it. */
  "#1e303a": ["elevation1"],
  "rgba(36,56,67,1)": ["elevation2"],
  "#354f5f": ["elevation3"],

  /* `default` has a grey for muted text where `default-table` fades white. */
  "#959fa6": ["fgSubtle"] /* → --color-fg-subtle */,
  "#8f989d": ["fgSubtle"],
  "rgba(149,159,166,0.1)": ["fgSubtle", 0.1],
  "rgba(149,159,166,0.5)": ["fgSubtle", 0.5],

  /* The same surfaces where BetBy fades them over something else. */
  "rgba(30,49,58,0.5)": ["elevation1", 0.5],
  "rgba(42,63,76,0.5)": ["elevation2", 0.5],
  "rgba(42,63,76,0.75)": ["elevation2", 0.75],

  /* Accent. Their blue is our violet — and they have two blues, because the
     `default` theme is a different palette from `default-table`. Both are here:
     an unknown theme name falls back to `default`, and a book that recoloured
     only its surfaces would come out blue-on-black, which reads as broken
     rather than as either design. */
  "#0989ff": ["brand"] /* brand, info     → --color-brand      */,
  "#21a8f7": ["brand"] /* the same, in `default`               */,
  "rgba(9,137,255,0.75)": ["brand", 0.75],
  "rgba(34,169,247,0.75)": ["brandBold", 0.75] /* hover → brand-bold */,
  "rgba(33,168,247,0.75)": ["brandBold", 0.75],
  "#0072b1": ["brandSubtle"] /* deeper accent   → brand-subtle       */,
  "#bc4eff": ["brandBold"] /* quick bet       → brand-bold         */,
  "#45c7fa": ["brand"],
  "#2196f3": ["brandSubtle"],

  /* Semantics. */
  "#7ed321": ["highlight"] /* success         → --color-highlight  */,
  "#41bc2c": ["success"] /* bonus           → --color-success    */,
  "rgba(65,188,44,0.75)": ["success", 0.75],
  "#ff4e4e": ["error"] /* danger          → --color-error      */,
  "#f5a623": ["warning"] /* warning         → --color-warning    */,
  "#ffb400": ["warning"],
  "#d3dbe0": ["fgMuted"] /* extra           → --color-fg-muted   */,
};

/* Corner rounding, so the book's edges match the kit's. Northstar panels are
   15px, controls 9px, small chips 6px and buttons full pills. Only plain
   values are touched — BetBy writes compound radii like `0 6px 0 6px` for a
   few joined elements, and reshaping those would break the join. */
const RADII: Record<string, number | string> = {
  "2": 4,
  "4": 6,
  "6": 9,
  "16": 15,
  "24": 100 /* buttons → the kit's --radius-rounded pills */,
  "2px": "4px",
  "4px": "6px",
  "6px": "9px",
  "8px": "9px",
  "16px": "15px",
};

const normalise = (value: string) => value.replace(/\s+/g, "").toLowerCase();

const isRadius = (key: string) => key.toLowerCase().includes("radius");

const COLOUR_PATTERN = /#[0-9a-f]{3,8}\b|rgba?\([^)]*\)/gi;

function channels(value: string): [number, number, number, string | undefined] {
  const functional = /^rgba?\(([^)]+)\)$/i.exec(value.trim());
  if (functional) {
    const [r, g, b, a] = functional[1].split(",").map((part) => part.trim());
    return [Number.parseInt(r, 10), Number.parseInt(g, 10), Number.parseInt(b, 10), a];
  }

  const hex = value.replace("#", "");
  const full = hex.length === 3 ? [...hex].map((c) => c + c).join("") : hex;
  return [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16),
    undefined,
  ];
}

const HUE_PALETTE: Record<string, ThemeColorKey> = Object.fromEntries(
  Object.entries(PALETTE)
    .filter(([, swatch]) => swatch.length === 1)
    .map(([betby, [key]]) => {
      const [r, g, b] = channels(betby);
      return [`${r},${g},${b}`, key];
    }),
);

function swatches(palette: Palette): Record<string, string> {
  return Object.fromEntries(
    Object.entries(PALETTE).map(([betby, [key, alpha]]) => {
      const colour = palette[key];
      if (alpha === undefined) return [betby, colour];
      const [r, g, b] = channels(colour);
      return [betby, `rgba(${r}, ${g}, ${b}, ${alpha})`];
    }),
  );
}

/* Walks the whole document rather than a list of known keys, so every one of
   the 305 entries is covered and any BetBy adds later is covered too. */
export function recolourTheme<T extends Record<string, unknown>>(theme: T, palette: Palette = DEFAULT_PALETTE): T {
  const resolved = swatches(palette);

  const recolourValue = (key: string, value: unknown): unknown => {
    if (Array.isArray(value)) {
      return value.map((entry) => recolourValue(key, entry));
    }

    if (value !== null && typeof value === "object") {
      return walk(value as Record<string, unknown>);
    }

    if (isRadius(key) && (typeof value === "number" || typeof value === "string")) {
      return RADII[String(value)] ?? value;
    }

    if (typeof value === "string") {
      return value.replace(COLOUR_PATTERN, (match) => {
        const exact = resolved[normalise(match)];
        if (exact !== undefined) return exact;

        const [r, g, b, alpha] = channels(match);
        const token = HUE_PALETTE[`${r},${g},${b}`];
        if (!token) return match;

        const hex = palette[token];
        if (alpha === undefined || Number.parseFloat(alpha) === 1) return hex;

        const [hr, hg, hb] = channels(hex);
        return `rgba(${hr}, ${hg}, ${hb}, ${alpha})`;
      });
    }

    return value;
  };

  const walk = <V extends Record<string, unknown>>(node: V): V =>
    Object.fromEntries(Object.entries(node).map(([key, value]) => [key, recolourValue(key, value)])) as V;

  return walk(theme);
}
